import {
  FILTER_TYPES,
  FilterItem,
  FilterParams,
} from '@mm/state/model/AudiencesModel';
import {
  MyLeadsFilterTypes,
  MyLeadsFriendlyName,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { isObject } from 'lodash';
import { F } from 'lodash/fp';
import moment from 'moment';
import pluralize from 'pluralize';
import {
  convertToUpperSnakeCase,
  convertToCamesCase,
} from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { YES_VALUE } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { Tag } from '../MyLeadsFilterBar/MyLeadsFilterBar';
import { Variable } from '@mm/state/interfaces/audiences';
import { PLATFORM_CAPTIONS } from '../../Audiences/FilterBar/FilterBarPlatformFilter';

export const convertObjectToMultidimensionalArray = (obj: {
  [key: string]: any;
}) => {
  const result: [string, any][] = [];

  Object.entries(obj).forEach(([key, val]) => {
    val.forEach((v: any) => {
      result.push([key, v]);
    });
  });

  return result;
};

export const getParamsByKeys = (variables, keysArr) => {
  const result = {};

  Object.keys(variables).forEach(key => {
    if (keysArr.includes(key)) {
      result[key] = variables[key];
    }
  });

  return result;
};

export const setPlaceholderByKey = (key: string) => {
  switch (key) {
    case SubsectionKeys.customerJourney[0]:
      return ['Minutes', 'Seconds'];
    case SubsectionKeys.customerJourney[1]:
      return ['Clicks'];
    case SubsectionKeys.customerJourney[2]:
      return ['Views'];
    case SubsectionKeys.customerJourney[3]:
      return ['Inches'];
    case SubsectionKeys.customerJourney[4]:
      return ['Visits'];
    default:
      return [''];
  }
};

export const remappedNames = (
  value: string,
  csvFriendlyName: string = 'Imported',
) => {
  const formattedValue =
    typeof value === 'string' ? value?.split('::')?.[0] : value;
  switch (formattedValue) {
    case 'x-ray':
      return { friendlyName: 'X-Ray', value: 'x-ray' };
    case 'csv':
      return { friendlyName: csvFriendlyName, value: 'csv' };
    case 'web_chat':
      return { friendlyName: 'Webchat', value: 'web_chat' };
    case 'LeadsDB':
    case 'leads_db':
      return { friendlyName: 'Consumer Directory Leads', value: 'leads_db' };
    case 'lgbtq+ donor/supporter':
      return { friendlyName: 'LGBTQ+ Donor/Supporter', value: formattedValue };
    case 'startDate':
    case 'start_date':
      return { friendlyName: 'Created from', value: 'start_date' };
    case 'endDate':
    case 'end_date':
      return { friendlyName: 'Created till', value: 'end_date' };
    case 'gt':
      return { friendlyName: 'greater than', value: 'gt' };
    case 'lt':
      return { friendlyName: 'less than', value: 'lt' };
    default:
      return { friendlyName: formattedValue, value: formattedValue };
  }
};

const getMinutesAndSeconds = (seconds: number) => {
  const duration = moment.duration(Number(seconds), 'seconds');
  const minutes = Math.floor(duration.asMinutes());
  const minutesText = pluralize('minute', minutes);
  const remainingSeconds = Math.floor(duration.asSeconds()) % 60;
  const secondsText = pluralize('second', remainingSeconds);
  if (minutes && remainingSeconds) {
    return `${minutes} ${minutesText} ${remainingSeconds} ${secondsText}`;
  }
  if (minutes) {
    return `${minutes} ${minutesText}`;
  }
  if (remainingSeconds) {
    return `${remainingSeconds} ${secondsText}`;
  }
};

export const mapQueryToTags = (
  filterParamObject,
  type: MyLeadsFilterTypes,
): Tag[] =>
  Object.entries(filterParamObject || {}).flatMap(
    ([key, values]: [string, (string | boolean)[]]) =>
      values.flatMap(value => {
        if (typeof value === 'string') {
          if (SubsectionKeys.location.includes(convertToCamesCase(key))) {
            return {
              key,
              value,
              type,
              fatTag:
                type === MyLeadsFilterTypes.excludedVariables
                  ? 'Exclude'
                  : 'Include',
              friendlyName: value,
            };
          }
          if (key === convertToUpperSnakeCase(SubsectionKeys.landingPageUrl)) {
            return {
              key,
              value,
              type,
              fatTag: MyLeadsFriendlyName[key],
              friendlyName: value,
              lowercase: true,
            };
          }
          if (key === SubsectionKeys.urlsVisited) {
            return {
              key,
              value,
              type,
              fatTag: MyLeadsFriendlyName[key],
              friendlyName: value,
              lowercase: true,
            };
          }

          return {
            key,
            value,
            friendlyName:
              value === YES_VALUE.toLocaleLowerCase()
                ? MyLeadsFriendlyName[key]
                : remappedNames(value).friendlyName,
            type,
          };
        }
        if (typeof value === 'boolean') {
          if (key === convertToUpperSnakeCase(SubsectionKeys.leadType)) {
            return {
              key,
              value,
              type,
              friendlyName:
                type === MyLeadsFilterTypes.excludedVariables
                  ? MyLeadsFriendlyName.CONSUMER_LEADS
                  : MyLeadsFriendlyName.BUSINESS_CONTACT,
            };
          }
        }
        if (typeof value === 'object') {
          if (key === 'created_at') {
            return Object.entries(value)
              .map(
                ([dateKey, dateValue]: [string, string]) =>
                  ({
                    key,
                    subKey: dateKey,
                    value: dateValue,
                    friendlyName: `${
                      remappedNames(dateKey).friendlyName
                    }: ${dateValue}`,
                    type,
                  } as Tag),
              )
              .filter(i => i.value) as Tag[];
          }
          if (SubsectionKeys.customerJourney.includes(key)) {
            const operator = isObject(value)
              ? Object.keys(value).map(v => remappedNames(v).friendlyName)?.[0]
              : '';
            const withOperator = operator ? ` ${operator}` : ':';
            const val = Object.values(value)?.[0];
            const formattedVal =
              key === SubsectionKeys.customerJourney[0]
                ? getMinutesAndSeconds(Number(val))
                : val;

            return {
              key,
              value,
              type,
              friendlyName: `${
                MyLeadsFriendlyName[key]
              }${withOperator} ${formattedVal} ${
                key !== SubsectionKeys.customerJourney[0]
                  ? setPlaceholderByKey(key)[0]
                  : ''
              }`,
            };
          }
        }

        return {
          key,
          value,
          friendlyName: MyLeadsFriendlyName[key],
          type,
        };
      }),
  );

export const mapFilterParamsToTags = (
  filterParams: FilterParams | undefined,
  audienceVariables: Variable[],
): Tag[] => {
  if (!filterParams || !filterParams?.every(f => f.value.length)) return [];

  return filterParams.map((filter: FilterItem) => {
    const isFilterByPlatform = filter.filterType === FILTER_TYPES.platforms;
    const variableName = audienceVariables.find(
      v => v.variableId === filter?.variableId,
    )?.friendlyName;
    return {
      key: filter.variableId || filter.filterType,
      value: filter.value,
      type: filter.filterType,
      friendlyName: Array.isArray(filter.value)
        ? filter.value
            .map(v => (isFilterByPlatform ? PLATFORM_CAPTIONS[v] : v))
            .join(', ')
        : isFilterByPlatform
        ? PLATFORM_CAPTIONS[filter.value]
        : filter.value,
      fatTag: `${
        isFilterByPlatform
          ? pluralize(filter.filterType, filter.value.length)
          : variableName
      }${!isFilterByPlatform ? ` ${filter.operator?.replace('_', ' ')}` : ''}`,
      lowercase: filter.filterType === 'variable_values',
    };
  });
};
